/*
 * One SEO page's record: what it targets, what search engines and AI assistants read on it, and
 * its history. The same EntityWorkspace every CC record uses: identity rail, section cards,
 * activity rail. Performance carries the Search Console and GA4 figures.
 */
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import type {
  GlanceRow,
  TimelineEntry,
  TimelineKind,
  WorkspaceTab,
} from '@components/Common/EntityWorkspace';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import ErrorMessage from '@components/Common/ErrorMessage';
import NotesSection from '@components/Common/NotesSection';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { addSeoNoteMutation } from '@constants/graphQLQueries';
import { formatDate, formatDateTime, formatRelative } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { htmlToText } from '@helpers/utils';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined';
import { Box, Chip } from '@mui/material';
import type { RootState } from '@store/rootReducer';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
  type SeoEvent,
  type SeoEventKind,
  type SeoKeyword,
  type SeoPage,
  changeText,
  fetchPageQueries,
  fetchPageRecord,
  fmtCount,
  fmtPosition,
  pathOf,
  plural,
  typeLabel,
} from './seoData';
import SeoKeywordChecks, { coverageLabel } from './SeoKeywordChecks';
import SeoPerformance from './SeoPerformance';
import YesNo from './YesNo';
import './index.scss';

const TABS: WorkspaceTab[] = [
  { id: 'overview', label: 'Overview', icon: <InfoOutlinedIcon /> },
  { id: 'performance', label: 'Performance', icon: <QueryStatsOutlinedIcon /> },
];

const TIMELINE_KIND: Record<SeoEventKind, TimelineKind> = {
  ADDED: 'submitted',
  CHANGED: 'status',
  REMOVED: 'status',
  NOTE: 'note',
};

const SITE = 'https://www.trymintly.com';

const chips = (items: string[], none: string) =>
  items.length ? (
    <Box className="seo-page__chips">
      {items.map((k) => (
        <Chip key={k} size="small" variant="outlined" label={k} />
      ))}
    </Box>
  ) : (
    none
  );

const SeoPageWorkspace: React.FC = () => {
  const { pageId = '' } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'overview');
  const [activityOpen, toggleActivity] = useActivityRail();
  const profile = useSelector(
    (state: RootState) => state.loginUserProfile.profile,
  );
  const byName =
    [profile?.personalDetails?.firstName, profile?.personalDetails?.lastName]
      .filter(Boolean)
      .join(' ') ||
    profile?.personalDetails?.email ||
    'Staff';

  const path = pathOf(pageId);
  const [page, setPage] = useState<SeoPage | null>(null);
  const [events, setEvents] = useState<SeoEvent[]>([]);
  const [queries, setQueries] = useState<SeoKeyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, q] = await Promise.all([
        fetchPageRecord(path),
        // Google's figures are a column, not the record: a failure leaves it blank.
        fetchPageQueries(path).catch(() => [] as SeoKeyword[]),
      ]);
      setPage(r.page);
      setEvents(r.events);
      setQueries(q);
    } catch {
      setError('This page could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    load();
  }, [load]);

  const onAddNote = async (noteHtml: string) => {
    const text = htmlToText(noteHtml).trim();
    if (!text) return;
    try {
      const r = await fetchGraphQLApiData(
        addSeoNoteMutation(path, text, byName),
      );
      const added = r?.data?.addSeoNote as SeoEvent | undefined;
      if (!added) throw new Error('no note');
      setEvents((prev) => [added, ...prev]);
      showToast('Note added', 'success');
    } catch {
      showToast('The note was not saved. Try again.', 'error');
    }
  };

  if (loading) return <SkeletonLoader variant="workspace" />;
  if (error) return <ErrorMessage page message={error} onRetry={load} />;
  if (!page)
    return (
      <EmptyState
        title="This page is not tracked"
        body="It may never have been synced, or the path is wrong."
      />
    );

  const removed = page.status === 'REMOVED';
  const glance: GlanceRow[] = [
    { key: 'Type', value: typeLabel(page.pageType) },
    { key: 'City', value: page.city ?? '-' },
    { key: 'Vertical', value: page.vertical ?? '-' },
    { key: 'Clicks (28d)', value: fmtCount(page.clicks28), mono: true },
    { key: 'Avg position', value: fmtPosition(page.position28), mono: true },
    { key: 'FAQs', value: String(page.faqCount ?? 0), mono: true },
    {
      key: 'Live jobs',
      value: page.liveJobs === null ? '-' : String(page.liveJobs),
      mono: true,
    },
    {
      key: 'Last change',
      value: formatRelative(page.lastChangedAt),
      mono: true,
    },
    { key: 'First seen', value: formatDate(page.firstSeenAt), mono: true },
  ];

  const identityRail = (
    <IdentityRail
      initials="SEO"
      name={page.path}
      subId={page.primaryKeyword ?? 'No primary keyword'}
      statusChip={
        removed ? (
          <CRMStatusChip status="closed" label="Removed from the site" />
        ) : (
          <CRMStatusChip
            status={page.indexable ? 'active' : 'inactive'}
            label={page.indexable ? 'Indexable' : 'Not indexed'}
          />
        )
      }
      badges={
        page.inLlms ? (
          <CRMStatusChip status="approved" label="In llms.txt" />
        ) : undefined
      }
      glance={glance}
    />
  );

  const overview = (
    <>
      {page.fetchError && (
        <ErrorMessage
          message={`The last sync could not read this page (${page.fetchError}). The facts below are from the last good read.`}
          onRetry={load}
          retryLabel="Reload"
        />
      )}
      <SectionCard
        title="Keywords"
        icon={<KeyOutlinedIcon />}
        meta={
          coverageLabel(page.keywordChecks) ??
          `${(page.primaryKeyword ? 1 : 0) + page.secondaryKeywords.length}`
        }
      >
        <SeoKeywordChecks
          checks={page.keywordChecks}
          queries={queries}
          hasKeywords={!!page.primaryKeyword || page.secondaryKeywords.length > 0}
        />
      </SectionCard>
      <SectionCard
        title="What search engines read"
        icon={<TitleOutlinedIcon />}
        meta={page.httpStatus ? `HTTP ${page.httpStatus}` : undefined}
      >
        <WorkspaceField label="Title" full value={page.title ?? '-'} />
        <WorkspaceField
          label="Meta description"
          full
          value={page.description ?? '-'}
        />
        <WorkspaceField label="H1" full value={page.h1 ?? '-'} />
        <WorkspaceField
          label="Canonical"
          full
          mono
          value={page.canonical ?? '-'}
        />
        <WorkspaceField label="Robots" mono value={page.robots ?? '-'} />
      </SectionCard>
      <SectionCard
        title="Answer engines (AEO)"
        icon={<QuestionAnswerOutlinedIcon />}
        meta={plural(page.schemaTypes.length, 'schema type')}
      >
        <WorkspaceField
          label="FAQ questions"
          mono
          value={String(page.faqCount ?? 0)}
        />
        <WorkspaceField
          label="Speakable"
          value={<YesNo on={page.schemaTypes.includes('Speakable')} />}
        />
        <WorkspaceField
          label="Structured data"
          full
          value={chips(page.schemaTypes, 'None')}
        />
      </SectionCard>
      <SectionCard
        title="AI assistants (GEO) and indexing"
        icon={<AutoAwesomeOutlinedIcon />}
      >
        <WorkspaceField
          label="Listed in llms.txt"
          value={<YesNo on={page.inLlms} />}
        />
        <WorkspaceField
          label="In sitemap"
          value={<YesNo on={page.inSitemap} />}
        />
        <WorkspaceField
          label="Indexable"
          value={<YesNo on={page.indexable} />}
        />
        <WorkspaceField
          label="URL"
          full
          mono
          value={
            <a
              className="cc-link"
              href={`${SITE}${page.path}`}
              target="_blank"
              rel="noreferrer"
            >
              www.trymintly.com{page.path}
            </a>
          }
        />
      </SectionCard>
    </>
  );

  const timeline: TimelineEntry[] = events.map((e) => ({
    id: e.id,
    kind: TIMELINE_KIND[e.kind] ?? 'status',
    text: e.kind === 'NOTE' ? (e.note ?? e.text) : e.text,
    body: e.changes?.length ? (
      <>
        {e.changes.map((c) => (
          <div key={c.field}>{changeText(c)}</div>
        ))}
      </>
    ) : undefined,
    meta: e.by ? `${formatDateTime(e.at)} · ${e.by}` : formatDateTime(e.at),
  }));

  return (
    <EntityWorkspace
      backLabel="SEO, GEO, AEO"
      onBack={() => navigate('/seo')}
      identityRail={identityRail}
      tabs={TABS}
      activeTabId={activeTab}
      onTabChange={setActiveTab}
      activityRail={
        <ActivityRail
          composer={<NotesSection notes={[]} isEditMode onAdd={onAddNote} />}
          timeline={timeline}
        />
      }
      activityOpen={activityOpen}
      onToggleActivity={toggleActivity}
    >
      {activeTab === 'performance' ? <SeoPerformance page={page} /> : overview}
    </EntityWorkspace>
  );
};

export default SeoPageWorkspace;
